import { useState } from 'react';
import type { JSX } from 'react';
import type { PaymentIntent } from '../types/api';
import type { Locale } from '../i18n';
import { renderableMethods } from '../session/uiState';
import { BrandMark } from './BrandMark';
import { gatewayLabel, widgetText } from './copy';
import { MerchantReturnLink } from './merchantReturn';
import { bundledGatewayLogo } from './gatewayAssets';
import {
  MUTED_TEXT_CLASS,
  PRIMARY_BUTTON_CLASS,
  SECONDARY_BUTTON_CLASS,
  STACK_CLASS,
  SURFACE_CLASS,
  TITLE_CLASS,
} from './styles';

export interface MethodSelectorProps {
  intent: PaymentIntent;
  locale: Locale;
  onSelect(gatewayId: string): void;
  /**
   * Vuelta a la tienda ANTES de pagar.
   *
   * <p>Sin esto el comprador que llega al selector y quiere corregir algo --el monto, el carrito, el
   * correo-- no tiene salida: el checkout alojado es una pagina nueva, asi que no hay «atras» del
   * comercio al que volver, y las pantallas terminales, que si ofrecen la vuelta, solo aparecen
   * cuando ya hay desenlace. El comprador quedaba atrapado en la unica pantalla donde todavia no ha
   * hecho nada.
   *
   * <p>Es el `merchant_return_url` del intent. Si el comercio no lo declaro no se pinta nada: ver
   * `MerchantReturnLink`, que explica por que no se intenta adivinar la salida.
   */
  merchantReturnUrl?: string | undefined;
  /**
   * Salida EMPOTRADA. Con el widget dentro de un iframe del comercio, se le avisa en vez de
   * llevarse la ventana entera a su propio sitio; ver `MerchantReturnLink`.
   */
  onReturnToMerchant?: (() => void) | undefined;
  /** Si el widget vive dentro de un iframe del comercio. */
  embedded?: boolean | undefined;
}

interface SelectableMethod {
  gatewayId: string;
  displayName: string | undefined;
  /** Logo de la pasarela, si el tenant lo publico en `public_config.logo_url`. */
  logoUrl: string | undefined;
}

/**
 * Metodos que este selector pinta: los de `renderableMethods`, PROYECTADOS a la vista.
 *
 * <p>Aqui no queda ni un gramo de la decision de que se ofrece. Esa regla —que pasarelas sabe
 * renderizar esta build, que hacer con las repetidas, y el respaldo a la pasarela activa de la
 * sesion— vive una sola vez, en `renderableMethods`, y el POR QUE de cada parte esta documentado
 * alli. El motivo de que este archivo no tenga su propia copia es concreto: `uiState` cuenta lo mismo
 * para decidir si se muestra el selector, y si las dos cuentas se separan el comprador acaba en un
 * selector de una sola opcion o directo en un flujo que este widget no puede completar.
 *
 * <p>Lo unico que se decide aqui es lo que SI es presentacion: de donde sale el logo y el nombre
 * visible de cada fila. Con la lista vacia el selector explica el caso con `method.none`, que es un
 * final legible y sin boton que prometa un pago imposible.
 */
function selectableMethods(intent: PaymentIntent): SelectableMethod[] {
  return renderableMethods(intent).map((method) => ({
    gatewayId: method.gateway_id,
    displayName: method.display_name,
    // Manda el tenant; si no configuro nada, el logo oficial que trae la plataforma.
    logoUrl: method.public_config['logo_url'] ?? bundledGatewayLogo(method.gateway_id),
  }));
}

/**
 * Fila seleccionable. El input real existe y es el que recibe el foco: se estiliza el contorno.
 *
 * <p>La fila crece a 60px de alto (py-3.5 + logo de 36) por dos motivos que apuntan al mismo sitio:
 * es un objetivo tactil comodo en movil, y da aire suficiente para que el estado seleccionado se
 * distinga de un vistazo sin depender solo del radio marcado.
 */
const OPTION_ROW_CLASS =
  'flex w-full cursor-pointer items-center gap-3 rounded-[calc(var(--apipay-radius,14px)-2px)] ' +
  'px-4 py-3.5 text-sm transition-all has-[:focus-visible]:outline has-[:focus-visible]:outline-2 ' +
  'has-[:focus-visible]:outline-offset-2';

/**
 * Seleccionado: teñido, anillo de 2px del color de marca y un pelin elevado.
 *
 * <p>Tres senales a la vez y ninguna es el color por si solo, que es lo que exige WCAG 1.4.1: el
 * radio marcado y el grosor del anillo distinguen la fila aunque el color no se perciba.
 */
const OPTION_SELECTED_CLASS =
  'bg-[var(--apipay-primary-050,#f5f8ff)] ring-2 ring-[var(--apipay-color-primary,#3a4fd9)] ' +
  'shadow-[0_1px_2px_rgb(16_24_40_/_0.06)]';

const OPTION_IDLE_CLASS =
  'ring-1 ring-black/[0.08] hover:ring-[var(--apipay-color-primary,#3a4fd9)] hover:bg-black/[0.015]';

/**
 * Eleccion del medio de pago: una lista de radios y UN boton para continuar.
 *
 * <p>Antes cada fila era un boton que saltaba directo al flujo de la pasarela, y el comprador
 * acababa pulsando dos veces —una para elegir y otra para pagar— sin que la primera le confirmara
 * nada. Con radios, elegir y confirmar son gestos distintos: se ve que hay varias opciones, cual
 * esta marcada, y que el pago no empieza hasta pulsar «Ir a pagar».
 */
export function MethodSelector({
  intent,
  locale,
  onSelect,
  merchantReturnUrl,
  onReturnToMerchant,
  embedded,
}: MethodSelectorProps): JSX.Element {
  const methods = selectableMethods(intent);
  // Preseleccionada la primera: en un checkout, obligar a elegir cuando ya hay un orden de
  // preferencia del comercio solo anade un paso.
  const [chosen, setChosen] = useState<string | null>(methods[0]?.gatewayId ?? null);

  const chosenMethod = methods.find((method) => method.gatewayId === chosen) ?? null;

  return (
    <section
      data-testid="method-selector"
      aria-label={widgetText(locale, 'method.title')}
      className={`${SURFACE_CLASS} ${STACK_CLASS}`}
    >
      <h2 className={TITLE_CLASS}>{widgetText(locale, 'method.title')}</h2>

      {methods.length === 0 ? (
        <p className={MUTED_TEXT_CLASS}>{widgetText(locale, 'method.none')}</p>
      ) : (
        <>
          {/*
            `grow`: la lista de medios se queda con el espacio sobrante de la tarjeta, y con eso el
            bloque de accion --«pagaras con», el boton y la vuelta a la tienda-- queda ANCLADO ABAJO.
            En modo pagina la tarjeta se estira para acabar a la altura de la columna de al lado
            (ver PAGE_MAIN_STRETCH_CLASS), y sin esto ese sobrante caia DEBAJO del boton: un vacio al
            final de la tarjeta que se lee como un error de maquetacion.

            Donde no sobra espacio --embebido, o una tarjeta que ya mide lo que su contenido-- no
            hace nada, porque no hay nada que repartir.
          */}
          <div
            role="radiogroup"
            aria-label={widgetText(locale, 'method.title')}
            className={`${STACK_CLASS} grow`}
          >
            {methods.map((method) => {
              const label = gatewayLabel(locale, method.gatewayId, method.displayName);
              const selected = method.gatewayId === chosen;
              return (
                <label
                  key={method.gatewayId}
                  data-testid={`method-option-${method.gatewayId}`}
                  data-selected={selected ? 'true' : 'false'}
                  className={`${OPTION_ROW_CLASS} ${selected ? OPTION_SELECTED_CLASS : OPTION_IDLE_CLASS}`}
                >
                  <input
                    type="radio"
                    name="apipay-method"
                    value={method.gatewayId}
                    checked={selected}
                    onChange={() => {
                      setChosen(method.gatewayId);
                    }}
                    className="size-4 shrink-0 accent-[var(--apipay-color-primary,#3a4fd9)]"
                  />
                  {/* Logo del tenant si lo configuro; si no, el oficial que trae la plataforma. */}
                  <BrandMark name={label} logoUrl={method.logoUrl} shape="square" size={36} />
                  <span className="min-w-0 flex-1 truncate font-medium">{label}</span>
                </label>
              );
            })}
          </div>

          {chosenMethod === null ? null : (
            <p className={MUTED_TEXT_CLASS} data-testid="method-chosen">
              {widgetText(locale, 'method.chosen', {
                method: gatewayLabel(locale, chosenMethod.gatewayId, chosenMethod.displayName),
              })}
            </p>
          )}

          <button
            type="button"
            data-testid="method-continue"
            className={PRIMARY_BUTTON_CLASS}
            disabled={chosen === null}
            onClick={() => {
              if (chosen !== null) {
                onSelect(chosen);
              }
            }}
          >
            {widgetText(locale, 'method.continue')}
          </button>
        </>
      )}

      {/*
        Fuera del bloque de arriba a proposito: la salida a la tienda tiene que estar TAMBIEN cuando
        la lista viene vacia. Ese es justo el caso en que el comprador no puede hacer absolutamente
        nada aqui, y era donde el encierro dolia mas.
      */}
      <MerchantReturnLink
        merchantReturnUrl={merchantReturnUrl}
        locale={locale}
        className={SECONDARY_BUTTON_CLASS}
        onEmbeddedReturn={onReturnToMerchant}
        embedded={embedded}
      />

      <p className={MUTED_TEXT_CLASS}>{widgetText(locale, 'method.hint')}</p>
    </section>
  );
}
